/**
 * ConfirmDialog — shared confirmation modal for tenant-side destructive
 * or state-changing actions.
 *
 * Replaces three things:
 *   - native window.confirm()   ugly browser popup, no loading state, no
 *                               tone / semantic styling
 *   - ad-hoc <Dialog> blocks    copy-pasted per page, visually drifting
 *   - nothing at all            (silent destructive actions — rare but
 *                               we want one shape available when we need)
 *
 * Anatomy (mirrors svdy-website's AdminConfirmDialog so the operator
 * console and the tenant console feel consistent):
 *
 *   title            short imperative ("Discard changes?", "Terminate?")
 *   description?     optional lead paragraph
 *   subject?         optional muted card identifying the affected item
 *                    (person's name + email + code, branch name, etc.)
 *   details?         optional second paragraph for consequences / scope
 *   [Cancel] [Confirm]  primary button tinted by `tone`
 *
 * Loading:
 *   - onConfirm can be sync or async
 *   - buttons disable + label swaps to `confirmLoadingLabel` while pending
 *   - resolve → onClose fires automatically
 *   - reject  → buttons re-enable, dialog stays open, caller surfaces
 *               the error through its own snackbar / toast
 *
 * Keyboard: ESC closes (when idle), Enter submits via MUI's focus trap.
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';

export type ConfirmTone = 'danger' | 'primary' | 'warning';

interface Props {
  open: boolean;
  title: string;
  /** Lead paragraph — rendered above the subject card. */
  description?: React.ReactNode;
  /**
   * Optional affected-item card rendered inside a muted rounded panel.
   * Keeps visual parity with the Admin Console's confirmation dialogs.
   */
  subject?: React.ReactNode;
  /** Second paragraph under the subject card — consequences / scope. */
  details?: React.ReactNode;
  /** Button color tone. Danger = red, warning = amber, primary = blue. */
  tone?: ConfirmTone;
  confirmLabel: string;
  confirmLoadingLabel?: string;
  cancelLabel?: string;
  /** Called on confirm click. Can return a promise; dialog waits. */
  onConfirm: () => void | Promise<void>;
  /** Called on cancel / backdrop / ESC, and after successful confirm. */
  onClose: () => void;
  /** A THIRD action, left of centre, for a refusal whose evidence lives elsewhere.
   *
   * Added 2026-09-01 for the delete refusals: "someone already approved a request on this process"
   * is only actionable if the admin can look at those requests, and the remedy (archive) is a
   * different action from the evidence (see them). A dialog that offers only the remedy makes the
   * admin take our word for the reason.
   *
   * Deliberately NOT a fourth tone or a second confirm: it navigates away, so it is a plain text
   * button and the primary action keeps its emphasis. */
  extraAction?: { label: string; onClick: () => void };
}

export const ConfirmDialog: React.FC<Props> = ({
  open,
  title,
  description,
  subject,
  details,
  tone = 'primary',
  extraAction,
  confirmLabel,
  confirmLoadingLabel,
  cancelLabel,
  onConfirm,
  onClose,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const resolvedCancelLabel = cancelLabel ?? t('common:actions.cancel');

  const handleConfirm = async () => {
    setLoading(true);
    try {
      await Promise.resolve(onConfirm());
      onClose();
    } catch {
      // Caller handles the error toast. We just re-enable the buttons
      // so the user can retry or cancel.
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (loading) return;
    onClose();
  };

  // MUI Button `color` maps cleanly to our tones, and `variant="contained"`
  // gives a filled CTA that reads as "this is THE action". Keep cancel
  // as a plain text button so the hierarchy is obvious.
  const confirmColor: 'error' | 'primary' | 'warning' =
    tone === 'danger' ? 'error' : tone === 'warning' ? 'warning' : 'primary';

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 600 }}>{title}</DialogTitle>
      <DialogContent>
        {description && (
          <DialogContentText sx={{ fontSize: '0.9375rem', mb: subject ? 2 : 0 }}>
            {description}
          </DialogContentText>
        )}
        {subject && (
          <Box
            sx={{
              bgcolor: 'action.hover',
              borderRadius: 1.5,
              p: 2,
              mb: details ? 2 : 0,
              wordBreak: 'break-word',
            }}
          >
            {subject}
          </Box>
        )}
        {details && (
          <DialogContentText sx={{ fontSize: '0.8125rem' }}>
            {details}
          </DialogContentText>
        )}
      </DialogContent>
      <DialogActions sx={{ pb: 2 }}>
        {extraAction && (
          <Button
            onClick={extraAction.onClick}
            disabled={loading}
            color="inherit"
            sx={{ mr: 'auto' }}
          >
            {extraAction.label}
          </Button>
        )}
        <Button
          onClick={handleClose}
          disabled={loading}
          color="inherit"
        >
          {resolvedCancelLabel}
        </Button>
        <Button
          onClick={handleConfirm}
          disabled={loading}
          color={confirmColor}
          variant="contained"
          autoFocus
        >
          {loading
            ? confirmLoadingLabel ?? `${confirmLabel}…`
            : confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmDialog;
